"use client";

import { useOrganization } from "@clerk/nextjs";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
	persistQueryClientRestore,
	persistQueryClientSubscribe,
} from "@tanstack/react-query-persist-client";
import { type ReactNode, useEffect, useRef, useState } from "react";

/**
 * Bump whenever a PERSISTED query's response SHAPE changes.
 *
 * These caches are restored from localStorage and the client runs with
 * `staleTime: Infinity`, so a payload written before a field existed is hydrated
 * as-is and never refetched to repair itself — readers then see the field as
 * undefined. (`prompts-filters` gaining `tags` is what surfaced this.)
 */
const PERSIST_BUSTER = "v2-prompt-tags";

const PERSISTED_KEY_PREFIXES = new Set([
	"brand",
	"brandkits",
	"prompts-filters",
	"topics",
	// Topic/prompt options for the content + optimization brief pickers. Small,
	// slow-changing metadata — persisting it lets those selects paint filled on
	// reload instead of empty, and the 5-min staleTime still revalidates behind it.
	"topic-prompts-picker",
	"enabled-platforms",
	"enabled-regions",
]);

const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;

const shouldDehydrateQuery = (query: {
	queryKey: readonly unknown[];
	state: { status: string };
}) =>
	query.state.status === "success" &&
	PERSISTED_KEY_PREFIXES.has(String(query.queryKey[0]));

export function QueryProvider({ children }: { children: ReactNode }) {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						staleTime: Infinity,
						gcTime: 10 * 60 * 1000,
						retry: 2,
						refetchOnWindowFocus: false,
					},
				},
			}),
	);

	const { organization } = useOrganization();
	const orgId = organization?.id ?? null;
	const prevOrgIdRef = useRef<string | null | undefined>(undefined);

	useEffect(() => {
		const currentOrgId = orgId;

		if (currentOrgId === null) return;

		if (prevOrgIdRef.current != null && prevOrgIdRef.current !== currentOrgId) {
			queryClient.clear();
		}

		prevOrgIdRef.current = currentOrgId;
	}, [orgId, queryClient]);

	useEffect(() => {
		if (!orgId || typeof window === "undefined") return;

		const persister = createSyncStoragePersister({
			storage: window.localStorage,
			key: `qux-rq-cache-${orgId}`,
		});

		let cancelled = false;
		let unsubscribe = () => {};

		persistQueryClientRestore({
			queryClient,
			persister,
			maxAge: PERSIST_MAX_AGE,
			buster: PERSIST_BUSTER,
		}).then(() => {
			if (cancelled) return;
			unsubscribe = persistQueryClientSubscribe({
				queryClient,
				persister,
				buster: PERSIST_BUSTER,
				dehydrateOptions: { shouldDehydrateQuery },
			});
		});

		return () => {
			cancelled = true;
			unsubscribe();
		};
	}, [orgId, queryClient]);

	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}
